// Barevná loga třetích stran (dopravci, hodnoticí platformy) ze spritu
// `src/img/logos.svg` — `injectSprite.js ensureLogos()` ho vkládá inline do
// <body>, takže `<use href="#bs-logo-…">` míří do stejného dokumentu (stejný
// vzor jako ikonový sprite, jen bez currentColor: barvy jsou součástí známky).
//
// ⚠️ Sprite log se od #313 netáhne z bundlu, ale z CDN až po `load`, takže
// `logo()` běžně vyrábí uzel dřív, než symbol existuje. Proto tu MUSÍ zůstat
// `viewBox` z `LOGO_RATIO`: drží rozměr slotu i s prázdným `<use>`, jinak by
// dorazení spritu posunulo layout. Modul se importuje i v Node (devcheck
// `logos.mjs`) → nesmí tahat `injectSprite.js` (ten linkuje `sprite.svg`).
//
// Proč vlastní loga místo nativních: Shoptet u metod dopravy servíruje vlastní
// obrázky (101×50 px, GLS dokonce PNG) — na retině rozmazané a v každé skupině
// jinak velké. Sprite drží vektor a jednu optickou výšku.
//
// Kde se používá: modal „Možnosti doručení", krok 2 pokladny, chip rychlého
// dopravce (osa detailu, košík, modal), pás hodnocení v patičce.

const NS = 'http://www.w3.org/2000/svg';

// Poměr stran symbolu (šířka/výška ořezaného viewBoxu) — vnější <svg> ho
// potřebuje jako `viewBox="0 0 ratio 1"`, jinak nemá intrinsic ratio a
// `width: auto` u dané výšky spadne na nulu.
// ⚠️ Musí sedět se `src/img/logos.svg` (generuje `tools/media/build-logos.mjs`);
// hlídá to devcheck `tools/devcheck/logos.mjs`.
export const LOGO_RATIO = {
  ppl: 6.855,
  gls: 2.934,
  fofr: 2,
  heureka: 5.419,
  zbozi: 5.254,
  google: 3.306,
  firmy: 4.856,
  jipos: 4.918,
};

// Optická korekce: logo s velkými verzálkami (PPL) působí při stejné výšce
// větší než logo s dlouhými dotahy (Zboží.cz) nebo rastr s okrajem (Fofr).
// Násobek výšky slotu, ať řada log vypadá vyrovnaně.
export const LOGO_SCALE = {
  ppl: 0.82,
  gls: 1,
  fofr: 1.12,
  heureka: 0.92,
  zbozi: 1,
  google: 0.92,
  firmy: 0.86,
  // znak domu je vysoký a písmo vedle něj menší, než by výška slibovala
  jipos: 1.09,
};

/** Zná sprite tohle logo? */
export function hasLogo(name) {
  return Object.prototype.hasOwnProperty.call(LOGO_RATIO, name);
}

/**
 * `<svg class="bs-logo bs-logo--ppl"><use href="#bs-logo-ppl"></use></svg>`
 * Výšku řídí CSS (`--bs-logo-h`), šířku dopočítá poměr stran.
 */
export function logo(name, className) {
  if (!hasLogo(name)) return null;
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'bs-logo bs-logo--' + name + (className ? ' ' + className : ''));
  svg.setAttribute('viewBox', '0 0 ' + LOGO_RATIO[name] + ' 1');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.style.setProperty('--bs-logo-scale', String(LOGO_SCALE[name]));
  const use = document.createElementNS(NS, 'use');
  use.setAttribute('href', '#bs-logo-' + name);
  svg.appendChild(use);
  return svg;
}

// Název metody dopravy → logo dopravce. Klíčujeme na jméno, protože `data-code`
// sdílí tři metody (`logistics-ppl`) a nadrozměrné varianty mají vlastní názvy;
// pořadí pravidel je nosné jen potud, že Fofr a PPL se v názvu nikdy nepotkají.
const CARRIER_RULES = [
  { re: /\bPPL\b/i, id: 'ppl' },
  { re: /\bGLS\b/i, id: 'gls' },
  { re: /\bFOFR\b/i, id: 'fofr' },
];

/** Logo dopravce podle názvu metody („PPL na adresu" → `ppl`), jinak null. */
export function carrierLogoId(name) {
  const text = String(name || '');
  for (const rule of CARRIER_RULES) {
    if (rule.re.test(text)) return rule.id;
  }
  return null;
}

/** Hotový uzel loga dopravce podle názvu metody (null = neznámý dopravce). */
export function carrierLogo(name, className) {
  const id = carrierLogoId(name);
  return id ? logo(id, className) : null;
}

/**
 * Naplní pilulku dopravce („s PPL" u termínu doručení) LOGEM místo textu
 * (zadání Tomáše 2026-09-05). Text zůstává jako `sr-only`, takže věta
 * „Doručíme v pondělí 7. 9. s PPL" dává čtečce pořád smysl; u neznámého
 * dopravce se nic nemění a pilulka zůstane textová.
 *
 * @param {Element} el pilulka s instanční třídou (`bs-ship__ctag`, `bs-buy__fast`…)
 * @param {string} carrier název dopravce nebo metody („PPL", „GLS na adresu")
 * @param {string} text původní text pilulky („s PPL")
 */
export function carrierTag(el, carrier, text) {
  const mark = carrierLogo(carrier, 'bs-ctag__logo');
  el.textContent = '';
  if (!mark) {
    el.textContent = text;
    return el;
  }
  el.classList.add('is-logo');
  el.appendChild(mark);
  const sr = document.createElement('span');
  sr.className = 'sr-only';
  sr.textContent = text;
  el.appendChild(sr);
  return el;
}
